import Subject from "@zebrabi/design-library/dist/lib/events/Subject";
import Subscriber from "@zebrabi/design-library/dist/lib/events/Subscriber";

/**
 * Adapter for the InlineToggleButton/ToggleButton* API that was removed from
 * @zebrabi/design-library (last shipped in 1.3.19). Renders the zbi-toggle-button web
 * component from @zebrabi/ui-components behind the pre-1.3.264 class-based API so the
 * existing call sites keep working unchanged.
 */

export interface ToggleButtonData {
    value: boolean;
}

export class ToggleButtonSubscriber extends Subscriber<ToggleButtonData> {
    update: (message: ToggleButtonData, action?: string) => void;
}

export interface InlineToggleButtonAttributes {
    id?: string;
    label: string;
    name: string;
}

interface ZbiToggleButtonElement extends HTMLElement {
    value: boolean;
    name: string;
    label: string;
    disabled: boolean;
}

export class InlineToggleButton extends Subject<ToggleButtonData> {
    private readonly element: ZbiToggleButtonElement;

    constructor(value: boolean, htmlAttributes: InlineToggleButtonAttributes) {
        super();
        this.element = document.createElement("zbi-toggle-button");
        this.element.value = !!value;
        this.element.label = htmlAttributes.label;
        this.element.name = htmlAttributes.name;
        if (htmlAttributes.id) {
            this.element.id = htmlAttributes.id;
        }
        this.element.addEventListener("zbi-change", (event: Event) => {
            const detail = (<CustomEvent>event).detail;
            this.notify({ value: detail?.value ?? this.element.value });
        });
        // legacy call sites drive the toggle through inputNode().checked
        Object.defineProperty(this.element, "checked", {
            get: () => this.element.value,
            set: (value: boolean) => {
                this.element.value = !!value;
            },
        });
    }

    public containerNode(): HTMLElement {
        return this.element;
    }

    public inputNode(): HTMLInputElement {
        return <HTMLInputElement>(<unknown>this.element);
    }

    public appendTo(selector: string | HTMLElement): void {
        const target = typeof selector === "string" ? document.querySelector(selector) : selector;
        target?.appendChild(this.element);
    }

    public appendToDocumentFragment(fragment: DocumentFragment): void {
        fragment.appendChild(this.element);
    }
}
